// Config validation schemas for risks, kept dependency-light (zod + db *types*
// only) so the derived field descriptors can be exported from `@unbox/api` and
// consumed by the frontend without dragging the DB client / drizzle into the
// browser bundle. The drizzle-backed RisksRepo lives in ./repo and re-exports
// everything here for back-compat.
import type { Risk, RiskModelType } from "@unbox/db";
import z, { type ZodType } from "zod/v4";

export type SUPPORTED_CONFIG_FIELD_TYPES = "stringArray" | "string" | "number";

const FIELD_VALIDATORS: Record<SUPPORTED_CONFIG_FIELD_TYPES, () => ZodType> = {
  string: () => z.string(),
  number: () => z.number(),
  stringArray: () => z.array(z.string()),
};

const DEBATE_MERCHANT_RISK_CONFIG = z
  .object({
    // Key order is display order — the risk config form derives its fields
    // from this shape, so the definition renders before the exceptions.
    // Both nullable — an empty value is stored as null, and the pipeline falls back to
    // the catalog risk's own value when the merchant hasn't overridden it (UNB-2723).
    description: FIELD_VALIDATORS.string().nullable(),
    hardcoded_rules: FIELD_VALIDATORS.string().nullable(),
  })
  .strict();

const DEBATE_RISK_CONFIG = z
  .object({
    description: FIELD_VALIDATORS.string(),
    hardcoded_rules: FIELD_VALIDATORS.string(),
    confidence_threshold: FIELD_VALIDATORS.number(),
  })
  .strict();

export const MERCHANT_RISK_CONFIG_SCHEMAS = {
  debate: { v1: DEBATE_MERCHANT_RISK_CONFIG },
  claims: {
    v1: z
      .object({
        approved_claims: FIELD_VALIDATORS.stringArray(),
      })
      .strict(),
  },
  omni: {
    v1: z
      .object({
        // The only per-merchant omni knob. Optional — an absent value means the
        // catalog default threshold applies.
        threshold: FIELD_VALIDATORS.number().optional(),
      })
      .strict(),
  },
  keyword: {
    v1: z
      .object({
        banned_keywords: FIELD_VALIDATORS.stringArray(),
      })
      .strict(),
  },
  required_text: {
    v1: z
      .object({
        expected_strings: FIELD_VALIDATORS.stringArray(),
      })
      .strict(),
  },
  music: { v1: null },
  ai_content: { v1: DEBATE_MERCHANT_RISK_CONFIG },
} as const;

// Risk-level config schemas, keyed by model_type. Distinct from the
// merchant-level MERCHANT_RISK_CONFIG_SCHEMAS above: these validate the catalog
// risk's own `config` column, not a per-merchant override. Model types without
// a schema (null) accept any config.
export const RISK_CONFIG_SCHEMAS = {
  omni: {
    v1: z
      .object({
        omni_risk_name: FIELD_VALIDATORS.string(),
        threshold: FIELD_VALIDATORS.number(),
      })
      .strict(),
  },
  claims: {
    v1: z
      .object({
        system_prompt: FIELD_VALIDATORS.string(),
      })
      .strict(),
  },
  debate: { v1: DEBATE_RISK_CONFIG },
  keyword: { v1: null },
  required_text: { v1: null },
  music: { v1: null },
  ai_content: { v1: DEBATE_RISK_CONFIG },
} as const;

type AvailableMerchantRiskVersions = keyof (typeof MERCHANT_RISK_CONFIG_SCHEMAS)[RiskModelType];
type AvailableRiskVersions = keyof (typeof RISK_CONFIG_SCHEMAS)[RiskModelType];

export const METADATA_DEPENDENT_RISKS: RiskModelType[] = ["required_text"];

export type MerchantRiskConfigByModelType = {
  [K in RiskModelType]: (typeof MERCHANT_RISK_CONFIG_SCHEMAS)[K][AvailableMerchantRiskVersions] extends z.ZodType
    ? z.infer<(typeof MERCHANT_RISK_CONFIG_SCHEMAS)[K][AvailableMerchantRiskVersions]>
    : Record<string, never>;
};

export type MerchantRiskConfig = MerchantRiskConfigByModelType[RiskModelType];

// Union of every config field key across all model-type schemas, derived from
// the zod shapes. The frontend maps this to readable names via an exhaustive
// Record, so adding a field to a schema is a compile error there until named.
type ConfigFieldKeyOf<Schemas> = {
  [M in keyof Schemas]: {
    [V in keyof Schemas[M]]: Schemas[M][V] extends { shape: infer Shape } ? keyof Shape & string : never;
  }[keyof Schemas[M]];
}[keyof Schemas];

export type ConfigFieldKey =
  | ConfigFieldKeyOf<typeof MERCHANT_RISK_CONFIG_SCHEMAS>
  | ConfigFieldKeyOf<typeof RISK_CONFIG_SCHEMAS>;

// The available fields for a model type's config: each field's key, data type,
// and whether the key may be omitted entirely, derived from the zod schema.
export type ConfigFieldDescriptor = { key: ConfigFieldKey; type: SUPPORTED_CONFIG_FIELD_TYPES; optional: boolean };

type FieldDef = { type?: string; innerType?: ZodType };

const classifyField = (field: ZodType): { type: SUPPORTED_CONFIG_FIELD_TYPES; optional: boolean } => {
  let def = (field as { def?: FieldDef }).def;
  let optional = false;
  while (def && (def.type === "optional" || def.type === "nullable")) {
    if (def.type === "optional") optional = true;
    def = (def.innerType as { def?: FieldDef } | undefined)?.def;
  }
  const type = def?.type === "array" ? "stringArray" : def?.type === "number" ? "number" : "string";
  return { type, optional };
};

const describeSchemas = <K extends string>(
  schemas: Record<K, { v1: { shape: Record<string, ZodType> } | null }>,
): Record<K, ConfigFieldDescriptor[]> => {
  const out = {} as Record<K, ConfigFieldDescriptor[]>;
  for (const key of Object.keys(schemas) as K[]) {
    const schema = schemas[key].v1;
    out[key] = schema
      ? Object.entries(schema.shape).map(([fieldKey, validator]) => ({
          key: fieldKey as ConfigFieldKey,
          ...classifyField(validator),
        }))
      : [];
  }
  return out;
};

// Field descriptors derived from the zod schemas — the single source of truth
// the frontend form is built from.
export const MERCHANT_RISK_CONFIG_FIELDS: Record<RiskModelType, ConfigFieldDescriptor[]> =
  describeSchemas(MERCHANT_RISK_CONFIG_SCHEMAS);
export const RISK_CONFIG_FIELDS: Record<RiskModelType, ConfigFieldDescriptor[]> = describeSchemas(RISK_CONFIG_SCHEMAS);

// Per model type: the config fields whose string value is scanned for `{var}`
// template variables that must resolve to merchant domain knowledge keys. Field
// keys are constrained to each model's config shape, so non-debate model types
// (no placeholder fields) skip validation entirely.
export const PLACEHOLDER_VALIDATED_FIELDS: {
  [K in RiskModelType]: (keyof MerchantRiskConfigByModelType[K] & string)[];
} = {
  debate: ["description"],
  claims: [],
  omni: [],
  keyword: [],
  required_text: [],
  music: [],
  ai_content: ["description"],
};

// Extracts `{var}` template variables from a config field's string value. The
// `(?<!\{)` lookbehind ignores a `{` preceded by another `{` so doubled/escaped
// braces like `{{double}` aren't treated as placeholders. Single source of truth
// shared by domain-knowledge validation (api) and the risk config editor (frontend).
export const extractPlaceholders = (value: string): string[] => {
  const out: string[] = [];
  for (const match of value.matchAll(/(?<!\{)\{(\w+)\}/g)) out.push(match[1]);
  return out;
};

// Validates a merchant_risks.config payload against the schema for the parent
// risk's model_type. Returns a list of human-readable error messages; empty
// array means valid.
export const validateMerchantRiskConfig = async (
  risk: Pick<Risk, "modelType" | "version">,
  config: Record<string, unknown>,
): Promise<string[]> => {
  const version = `v${risk.version}` as AvailableMerchantRiskVersions;
  const schema = MERCHANT_RISK_CONFIG_SCHEMAS[risk.modelType]?.[version];

  if (!schema) return [];
  const res = await schema.safeParseAsync(config);
  if (!res.error) return [];
  return [res.error.message];
};

// Validates a risks.config payload against the risk-level schema for the risk's
// model_type. Returns a list of human-readable error messages; empty array
// means valid (including model types with no schema).
export const validateRiskConfig = async (
  risk: Pick<Risk, "modelType" | "version">,
  config: Record<string, unknown>,
): Promise<string[]> => {
  const version = `v${risk.version}` as AvailableRiskVersions;
  const schema = RISK_CONFIG_SCHEMAS[risk.modelType]?.[version];

  if (!schema) return [];
  const res = await schema.safeParseAsync(config);
  if (!res.error) return [];
  return [res.error.message];
};
